/**
 * GCS URI Mapper Utility
 *
 * Maps local video file paths and segment patterns to Google Cloud Storage URIs.
 * Used by useD4RTData to resolve video references for the D4RT 4D analysis service.
 */

const GCS_BUCKET = process.env.REACT_APP_GCS_BUCKET || 'slam-d4rt-videos';
const GCS_PREFIX = `gs://${GCS_BUCKET}`;

/**
 * Check if a string is a valid GCS URI (gs://bucket/path)
 * @param {string} uri
 * @returns {boolean}
 */
export function isValidGCSUri(uri) {
  if (!uri || typeof uri !== 'string') return false;
  return uri.startsWith('gs://');
}

/**
 * Map a local file path or segment pattern to a GCS URI.
 * Handles patterns like:
 *   - seg_0001.mp4 → gs://bucket/segments/seg_0001.mp4
 *   - /path/to/video.mp4 → gs://bucket/uploads/video.mp4
 *   - https://storage.googleapis.com/bucket/path → gs://bucket/path
 *
 * Returns null if mapping is not applicable (pass through as-is).
 * @param {string} videoPath
 * @returns {string|null}
 */
export function mapToGCSUri(videoPath) {
  if (!videoPath || typeof videoPath !== 'string') return null;

  // Already a GCS URI
  if (isValidGCSUri(videoPath)) return videoPath;

  // Convert HTTPS storage URLs to gs:// format
  const storageMatch = videoPath.match(
    /^https?:\/\/storage\.googleapis\.com\/([^/]+)\/(.+)$/
  );
  if (storageMatch) {
    return `gs://${storageMatch[1]}/${storageMatch[2]}`;
  }

  // Segment pattern: seg_NNNN.mp4
  const segMatch = videoPath.match(/^(seg_\d+\.mp4)$/i);
  if (segMatch) {
    return `${GCS_PREFIX}/segments/${segMatch[1]}`;
  }

  // Bare filename with video extension
  const filenameMatch = videoPath.match(/^([^/]+\.(mp4|mov|avi|webm))$/i);
  if (filenameMatch) {
    return `${GCS_PREFIX}/uploads/${filenameMatch[1]}`;
  }

  // Absolute or relative path — extract filename
  const pathMatch = videoPath.match(/[/\\]([^/\\]+\.(mp4|mov|avi|webm))$/i);
  if (pathMatch) {
    return `${GCS_PREFIX}/uploads/${pathMatch[1]}`;
  }

  // No mapping applicable
  return null;
}

export default { mapToGCSUri, isValidGCSUri };
